import { useEffect, useState, useCallback } from "react";
import {
  Code2, Copy, Check, Key, RefreshCw, ExternalLink, Download,
  Monitor, Smartphone, Globe, Trash2, Eye, AlertCircle, Link2,
  Lock, Unlock, Zap, ArrowUpRight, Radio, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../context/AuthContext";
import { EmbedAnalytics } from "./EmbedAnalytics";

export const EmbedTab = ({ currentUser, authToken }) => {
  const [apiKey, setApiKey] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [vanityData, setVanityData] = useState(null);
  const [activePreview, setActivePreview] = useState("full");
  const [embedTab, setEmbedTab] = useState("full");

  const headers = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (authToken) h["Authorization"] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/embed/my-key`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data.has_key) {
          setApiKey(data.api_key);
          setHasKey(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    if (!apiKey) return;
    fetch(`${BACKEND_URL}/api/embed/${apiKey}/vanity-url`)
      .then((r) => r.json())
      .then(setVanityData)
      .catch(() => {});
  }, [apiKey]);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/embed/generate-key`, {
        method: "POST",
        headers: headers(),
      });
      if (res.ok) {
        const data = await res.json();
        setApiKey(data.api_key);
        setHasKey(true);
        toast.success("API key generated!");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const revokeKey = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/embed/revoke-key`, {
        method: "DELETE",
        headers: headers(),
      });
      setApiKey(null);
      setHasKey(false);
      setVanityData(null);
      setShowRevokeConfirm(false);
      toast.success("API key revoked");
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
    // Also set visual state on button
    setCopiedBtn(label);
    setTimeout(() => setCopiedBtn(""), 2000);
  };
  const [copiedBtn, setCopiedBtn] = useState("");

  const siteUrl = "https://djsandmcmedia.com";
  const vanityUrl = vanityData?.vanity_url || "";
  const wlSlug = vanityData?.slug || "";

  const fullIframeTag = apiKey
    ? `<iframe src="${siteUrl}/api/embed/${apiKey}/full-player" style="width:100%;height:100vh;min-height:700px;border:none" allow="autoplay" loading="lazy"></iframe>`
    : "";
  const scriptTag = apiKey
    ? `<script src="${siteUrl}/api/embed/${apiKey}/player.js"></script>`
    : "";
  const miniIframeTag = apiKey
    ? `<iframe src="${siteUrl}/api/embed/${apiKey}/player" style="width:100%;max-width:400px;height:260px;border:none;border-radius:16px" allow="autoplay" loading="lazy"></iframe>`
    : "";

  const fullPreviewUrl = apiKey ? `${BACKEND_URL}/api/embed/${apiKey}/full-player` : "";
  const miniPreviewUrl = apiKey ? `${BACKEND_URL}/api/embed/${apiKey}/player` : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="embed-tab-loading">
        <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="embed-tab">
      {/* Hero — Emotional trigger */}
      <div className="bg-gradient-to-br from-purple-500/10 via-indigo-600/5 to-transparent border border-purple-500/20 rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Radio className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-1" data-testid="embed-hero-title">
              Turn your website into your own radio station.
            </h2>
            <p className="text-white/40 text-sm leading-relaxed max-w-lg">
              Your station. Your audience. Everywhere.
            </p>
          </div>
        </div>
      </div>

      {/* AzuraCast Setup Tip */}
      {hasKey && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5" data-testid="azuracast-tip">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-1">Pro Tip: AzuraCast Public API Settings</h4>
              <p className="text-white/40 text-xs leading-relaxed">
                In your AzuraCast station settings, <span className="text-white/60 font-medium">disable "Enable Public Pages"</span> and <span className="text-white/60 font-medium">enable "Enable Public APIs"</span>. This hides AzuraCast's default player while keeping your branded DJsandMCMedia embed working perfectly. Your listeners only see YOUR player — not the default AzuraCast page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Analytics — with "Where Your Listeners Come From" framing */}
      {hasKey && <EmbedAnalytics authToken={authToken} apiKey={apiKey} />}

      {/* Vanity URL */}
      {hasKey && vanityData?.slug && (
        <div className="bg-gradient-to-r from-green-500/5 to-emerald-500/5 border border-green-500/20 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <Link2 className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-bold text-white">Your Station URL</h3>
          </div>
          <p className="text-white/40 text-xs mb-4 ml-8">
            Share this link anywhere — social media, email, business cards. It's yours.
          </p>
          <div className="flex gap-2">
            <div className="flex-1 bg-black/40 border border-green-500/20 rounded-xl px-4 py-3 font-mono text-sm text-green-400 truncate">
              {vanityUrl}
            </div>
            <button
              onClick={() => copyToClipboard(vanityUrl, "vanity")}
              className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-4 py-3 rounded-xl transition flex items-center gap-2 text-sm font-medium flex-shrink-0"
              data-testid="embed-copy-vanity-btn"
            >
              {copiedBtn === "vanity" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-white/30 text-xs mt-2 ml-1">
            Also works: <span className="text-white/50 font-mono">{siteUrl}/player/{wlSlug}</span>
          </p>
        </div>
      )}

      {/* API Key */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Key className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-bold text-white">Your API Key</h3>
        </div>
        {!hasKey ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-white/50 text-sm mb-6 max-w-sm mx-auto">
              Generate your API key to start embedding your station on any website.
            </p>
            <button
              onClick={generateKey} disabled={generating}
              className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-medium transition"
              data-testid="embed-generate-key-btn"
            >
              {generating ? "Generating..." : "Generate API Key"}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-3">
              <div className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 font-mono text-sm text-amber-400 truncate">
                {apiKey}
              </div>
              <button
                onClick={() => copyToClipboard(apiKey, "key")}
                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 px-4 py-3 rounded-xl transition flex items-center gap-2 text-sm font-medium flex-shrink-0"
                data-testid="embed-copy-key-btn"
              >
                {copiedBtn === "key" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-white/30 text-xs">Keep this key secret.</p>
              <button
                onClick={() => setShowRevokeConfirm(true)}
                className="text-red-400/60 hover:text-red-400 text-xs flex items-center gap-1 transition"
                data-testid="embed-revoke-btn"
              >
                <Trash2 className="w-3 h-3" /> Revoke
              </button>
            </div>
            {showRevokeConfirm && (
              <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-300 text-sm font-medium">Revoke this key?</p>
                    <p className="text-red-300/60 text-xs mt-1">All embeds using this key will stop working.</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={revokeKey}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-medium transition"
                        data-testid="embed-revoke-confirm-btn">Yes, Revoke</button>
                      <button onClick={() => setShowRevokeConfirm(false)}
                        className="bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg text-xs transition">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tier Comparison + Upgrade CTA */}
      {hasKey && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-bold text-white">Embed vs White Label</h3>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10 rounded-xl overflow-hidden text-xs">
            <div className="bg-gray-950 p-3 font-medium text-white/60">Feature</div>
            <div className="bg-gray-950 p-3 font-bold text-center text-purple-400">Embed</div>
            <div className="bg-gray-950 p-3 font-bold text-center text-yellow-400">White Label</div>
            {[
              ["Full Player", true, true],
              ["Chat & Requests", true, true],
              ["Song History", true, true],
              ["Now Playing", true, true],
              ["Follower System", true, true],
              ["Station Branding", true, true],
              ['"Powered by" Badge', "Always", "upgrade"],
              ["Custom Domain", false, true],
              ["Full Layout Control", false, true],
              ["SEO Ownership", false, true],
              ["Priority Resources", false, true],
            ].map(([feature, embed, wl], i) => (
              <div key={i} className="contents">
                <div className="bg-gray-950 p-3 text-white/50">{feature}</div>
                <div className="bg-gray-950 p-3 text-center">
                  {embed === true ? <Unlock className="w-3.5 h-3.5 text-green-400 mx-auto" /> :
                   embed === false ? <Lock className="w-3.5 h-3.5 text-white/20 mx-auto" /> :
                   <span className="text-white/40">{embed}</span>}
                </div>
                <div className="bg-gray-950 p-3 text-center">
                  {wl === true ? <Unlock className="w-3.5 h-3.5 text-green-400 mx-auto" /> :
                   wl === "upgrade" ? <span className="text-yellow-400 text-[10px] cursor-pointer hover:underline">Removable</span> :
                   <span className="text-white/40">{wl}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Upgrade CTA Box */}
          <div className="mt-4 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20 rounded-xl p-5 flex items-center justify-between"
            data-testid="embed-upgrade-cta">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-white font-medium text-sm">Remove DJsandMCMedia branding and fully own your station</p>
                <p className="text-white/40 text-xs mt-0.5">Custom domain, full layout control, SEO ownership, priority streaming</p>
              </div>
            </div>
            <a href="/#pricing" className="bg-yellow-500 hover:bg-yellow-400 text-black px-5 py-2.5 rounded-xl text-sm font-bold transition whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              data-testid="embed-upgrade-btn">
              Upgrade to White Label <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}

      {/* Embed Codes — TABBED */}
      {hasKey && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Code2 className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-bold text-white">Embed Code</h3>
            </div>
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <button onClick={() => setEmbedTab("full")}
                className={`px-4 py-1.5 rounded-md text-xs font-medium transition ${embedTab === "full" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                data-testid="embed-code-tab-full">Full Station</button>
              <button onClick={() => setEmbedTab("mini")}
                className={`px-4 py-1.5 rounded-md text-xs font-medium transition ${embedTab === "mini" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                data-testid="embed-code-tab-mini">Mini Widget</button>
            </div>
          </div>

          {embedTab === "full" ? (
            <div>
              <p className="text-white/40 text-xs mb-3">
                Full experience: player, chat, requests, history, schedule. Paste this anywhere.
              </p>
              <div className="relative">
                <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-purple-400 text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">
                  {fullIframeTag}
                </pre>
                <button
                  onClick={() => copyToClipboard(fullIframeTag, "full")}
                  className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg transition"
                  data-testid="embed-copy-full-btn"
                >
                  {copiedBtn === "full" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-white/40 text-xs mb-1">
                Compact now-playing widget. Great for sidebars, footers, and landing pages.
              </p>
              <div className="relative">
                <p className="text-white/30 text-[10px] mb-1 font-medium">SCRIPT TAG</p>
                <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-green-400 text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">
                  {scriptTag}
                </pre>
                <button
                  onClick={() => copyToClipboard(scriptTag, "script")}
                  className="absolute top-7 right-3 bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg transition"
                  data-testid="embed-copy-script-btn"
                >
                  {copiedBtn === "script" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <p className="text-white/30 text-[10px] mb-1 font-medium">IFRAME</p>
                <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-blue-400 text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">
                  {miniIframeTag}
                </pre>
                <button
                  onClick={() => copyToClipboard(miniIframeTag, "iframe")}
                  className="absolute top-7 right-3 bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg transition"
                  data-testid="embed-copy-iframe-btn"
                >
                  {copiedBtn === "iframe" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Test Embed button */}
          <div className="mt-4 flex items-center gap-3">
            <a
              href={embedTab === "full" ? fullPreviewUrl : miniPreviewUrl}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 text-purple-400 px-4 py-2 rounded-xl text-xs font-medium transition"
              data-testid="embed-test-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Test Embed Live
            </a>
            <button
              onClick={() => copyToClipboard(embedTab === "full" ? fullIframeTag : scriptTag, embedTab === "full" ? "full" : "script")}
              className="inline-flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 px-4 py-2 rounded-xl text-xs font-medium transition"
              data-testid="embed-copy-quick-btn"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Embed Code
            </button>
          </div>
        </div>
      )}

      {/* WordPress Plugin */}
      {hasKey && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-1">
            <svg className="w-5 h-5 text-[#21759b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zM3.443 12c0-1.174.244-2.29.68-3.303l3.744 10.262A8.564 8.564 0 013.443 12zm8.557 8.557c-.882 0-1.73-.143-2.524-.407l2.68-7.786 2.747 7.525a.853.853 0 00.065.127 8.553 8.553 0 01-2.968.541zm1.203-12.564c.538-.028 1.022-.085 1.022-.085.481-.057.425-.764-.057-.736 0 0-1.447.114-2.379.114-.878 0-2.353-.114-2.353-.114-.482-.028-.538.708-.057.736 0 0 .457.057.94.085l1.396 3.826-1.963 5.886L7.06 7.993c.538-.028 1.022-.085 1.022-.085.481-.057.425-.764-.057-.736 0 0-1.447.114-2.379.114-.167 0-.364-.004-.571-.01A8.533 8.533 0 0112 3.443c2.369 0 4.531.965 6.088 2.524-.039-.002-.076-.008-.116-.008-.878 0-1.5.764-1.5 1.586 0 .736.425 1.358.878 2.094.34.594.737 1.358.737 2.46 0 .764-.293 1.65-.68 2.886l-.892 2.978-3.232-9.612zm3.963 13.076l2.724-7.872c.51-1.272.68-2.289.68-3.195 0-.328-.021-.633-.062-.916A8.538 8.538 0 0120.557 12a8.545 8.545 0 01-3.391 6.069z" />
            </svg>
            <h3 className="text-lg font-bold text-white">WordPress Plugin</h3>
          </div>
          <p className="text-white/40 text-xs mb-4 ml-8">
            One plugin, full station on any WordPress site. 2-minute setup.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-white/60 text-xs font-medium mb-2">Shortcodes</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <code className="bg-white/5 px-3 py-1.5 rounded-lg text-purple-400 text-xs font-mono flex-1">[djsandmc_player]</code>
                  <button onClick={() => copyToClipboard("[djsandmc_player]", "wp")} className="text-white/40 hover:text-white transition p-1">
                    {copiedBtn === "wp" ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="bg-white/5 px-3 py-1.5 rounded-lg text-purple-400 text-xs font-mono flex-1">[djsandmc_nowplaying]</code>
                  <button onClick={() => copyToClipboard("[djsandmc_nowplaying]", "wpnp")} className="text-white/40 hover:text-white transition p-1">
                    {copiedBtn === "wpnp" ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-white/60 text-xs font-medium mb-2">Installation</p>
              <ol className="text-white/40 text-xs space-y-1.5 list-decimal list-inside">
                <li>Download the plugin .zip below</li>
                <li>WordPress &rarr; Plugins &rarr; Add New &rarr; Upload</li>
                <li>Activate &rarr; Settings &rarr; DJsandMCMedia</li>
                <li>Paste your API key</li>
                <li>Add <code className="text-purple-400">[djsandmc_player]</code> to any page</li>
              </ol>
            </div>
          </div>
          <a
            href={`${BACKEND_URL}/api/embed/download/wordpress-plugin`}
            onClick={(e) => {
              e.preventDefault();
              fetch(`${BACKEND_URL}/api/embed/download/wordpress-plugin`, { headers: headers() })
                .then((r) => r.blob())
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "djsandmcmedia-player.zip";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Plugin downloaded!");
                })
                .catch(console.error);
            }}
            className="inline-flex items-center gap-2 bg-[#21759b] hover:bg-[#1a5f7e] text-white px-6 py-3 rounded-xl font-medium transition"
            data-testid="embed-download-wp-btn"
          >
            <Download className="w-4 h-4" /> Download WordPress Plugin
          </a>
        </div>
      )}

      {/* Live Preview — Fixed "Player Not Found" */}
      {hasKey && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-bold text-white">Live Preview</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1 mr-2">
                <button onClick={() => setActivePreview("full")}
                  className={`px-3 py-1.5 rounded-md text-xs transition ${activePreview === "full" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                  data-testid="embed-preview-full-btn">Full</button>
                <button onClick={() => setActivePreview("mini")}
                  className={`px-3 py-1.5 rounded-md text-xs transition ${activePreview === "mini" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                  data-testid="embed-preview-mini-btn">Mini</button>
              </div>
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                <button onClick={() => setPreviewMode("desktop")}
                  className={`p-2 rounded-md transition ${previewMode === "desktop" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                  data-testid="embed-preview-desktop"><Monitor className="w-4 h-4" /></button>
                <button onClick={() => setPreviewMode("mobile")}
                  className={`p-2 rounded-md transition ${previewMode === "mobile" ? "bg-purple-500/20 text-purple-400" : "text-white/40 hover:text-white"}`}
                  data-testid="embed-preview-mobile"><Smartphone className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          <div className={`bg-black/40 rounded-xl overflow-hidden mx-auto transition-all ${
            previewMode === "mobile" ? "max-w-[375px]" : activePreview === "full" ? "max-w-full" : "max-w-[420px]"
          }`}>
            {activePreview === "full" && fullPreviewUrl ? (
              <iframe src={fullPreviewUrl} className="w-full border-0"
                style={{ height: previewMode === "mobile" ? "600px" : "700px", borderRadius: "12px" }}
                title="Full Player Preview" allow="autoplay" data-testid="embed-preview-full-iframe" />
            ) : miniPreviewUrl ? (
              <iframe src={miniPreviewUrl} className="w-full border-0"
                style={{ height: "260px", borderRadius: "12px" }}
                title="Mini Player Preview" allow="autoplay" data-testid="embed-preview-mini-iframe" />
            ) : (
              <div className="flex flex-col items-center justify-center h-[260px] text-center px-4">
                <Radio className="w-8 h-8 text-white/10 mb-3" />
                <p className="text-white/30 text-sm font-medium">Preview your player after activating your station</p>
                <p className="text-white/20 text-xs mt-1">Set up your White Label Player in the dashboard first</p>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center">
            <a href={activePreview === "full" ? fullPreviewUrl : miniPreviewUrl}
              target="_blank" rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 text-xs flex items-center gap-1 transition"
              data-testid="embed-open-preview">
              <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
